import { useState } from "react";

interface Step {
  step_id: number;
  timestamp: string;
  source: string;
  message: string;
  tool_calls?: Array<{
    tool_call_id: string;
    function_name: string;
    arguments: any;
  }>;
  observation?: {
    results: Array<{
      content: string;
      source_call_id?: string;
    }>;
  };
  metrics?: {
    prompt_tokens: number;
    completion_tokens: number;
    cached_tokens: number;
  };
  extra?: {
    tool_result_metadata?: {
      tool_use_result?: {
        stdout?: string;
        stderr?: string;
        interrupted?: boolean;
        isImage?: boolean;
      };
    };
  };
}

interface Trajectory {
  steps: Step[];
  final_metrics?: {
    total_steps: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_cached_tokens: number;
  };
}

/**
 * Format duration in milliseconds to a human-readable string.
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(2);
    return `${minutes}m ${seconds}s`;
  }
}

/**
 * Get color class based on step duration.
 * - Green: < 2 seconds (fast)
 * - Orange: 2-10 seconds (medium)
 * - Red: > 10 seconds (slow)
 */
function getDurationColorClass(durationMs: number): string {
  if (durationMs < 2000) {
    return "text-green-600";
  } else if (durationMs < 10000) {
    return "text-orange-600";
  } else {
    return "text-red-600";
  }
}

/**
 * Get border color class based on step duration.
 */
function getDurationBorderClass(durationMs: number): string {
  if (durationMs < 2000) {
    return "border-green-500";
  } else if (durationMs < 10000) {
    return "border-orange-500";
  } else {
    return "border-red-500";
  }
}

export function TrajectoryView({ trajectory }: { trajectory: Trajectory }) {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]));

  const toggleStep = (stepId: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const steps = trajectory.steps || [];

  // Calculate step durations and total duration
  const stepDurations: Map<number, number> = new Map();
  let totalDuration = 0;

  if (steps.length > 0) {
    const firstStep = steps[0];
    const lastStep = steps[steps.length - 1];
    if (firstStep && lastStep) {
      const firstTimestamp = new Date(firstStep.timestamp).getTime();
      const lastTimestamp = new Date(lastStep.timestamp).getTime();
      totalDuration = lastTimestamp - firstTimestamp;

      // Calculate duration for each step (time since previous step)
      for (let i = 0; i < steps.length; i++) {
        const currentStep = steps[i];
        if (!currentStep) continue;
        
        const currentTimestamp = new Date(currentStep.timestamp).getTime();
        if (i === 0) {
          stepDurations.set(currentStep.step_id, 0);
        } else {
          const previousStep = steps[i - 1];
          if (previousStep) {
            const previousTimestamp = new Date(previousStep.timestamp).getTime();
            const duration = currentTimestamp - previousTimestamp;
            stepDurations.set(currentStep.step_id, duration);
          }
        }
      }
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Execution Trajectory</h3>
        {totalDuration > 0 && (
          <div className="text-sm text-gray-600">
            Total Duration: <span className="font-semibold text-gray-900">{formatDuration(totalDuration)}</span>
          </div>
        )}
      </div>
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {steps.map((step, index) => {
          const isExpanded = expandedSteps.has(step.step_id);
          const isUser = step.source === "user";
          const isAgent = step.source === "agent";
          const hasTools = step.tool_calls && step.tool_calls.length > 0;
          const stepDuration = stepDurations.get(step.step_id) || 0;
          const durationColorClass = getDurationColorClass(stepDuration);
          const durationBorderClass = getDurationBorderClass(stepDuration);

          // Determine base border color (user/agent/sidechain)
          let baseBorderClass = "border-gray-300";
          if (isUser) {
            baseBorderClass = "border-blue-300";
          } else if (isAgent) {
            baseBorderClass = "border-green-300";
          }

          // Override with duration-based border if step is slow
          const finalBorderClass = stepDuration >= 2000 ? durationBorderClass : baseBorderClass;

          return (
            <div
              key={step.step_id}
              className={`border-l-4 rounded p-3 ${
                isUser
                  ? "bg-blue-50 border-blue-300"
                  : isAgent
                  ? "bg-green-50 border-green-300"
                  : "bg-gray-50 border-gray-300"
              } ${finalBorderClass}`}
            >
              <div
                className="flex items-start justify-between cursor-pointer"
                onClick={() => toggleStep(step.step_id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-gray-600">
                      Step {step.step_id}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(step.timestamp).toLocaleTimeString()}
                    </span>
                    {stepDuration > 0 && (
                      <span className={`text-xs font-semibold ${durationColorClass}`}>
                        {formatDuration(stepDuration)}
                      </span>
                    )}
                    {hasTools && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded border border-purple-200">
                        {step.tool_calls?.length} tool{step.tool_calls?.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {step.metrics && (
                      <span className="text-xs text-gray-600">
                        {step.metrics.completion_tokens} tokens
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <div className="space-y-2">
                      <div className="text-sm whitespace-pre-wrap break-words">
                        {step.message}
                      </div>
                      {hasTools && (
                        <div className="mt-2 space-y-2">
                          {step.tool_calls?.map((tool, idx) => (
                            <div
                              key={tool.tool_call_id}
                              className="bg-gray-50 border border-gray-200 rounded p-2 text-xs"
                            >
                              <div className="font-semibold text-purple-700 mb-1">
                                {tool.function_name}
                              </div>
                              {tool.arguments && (
                                <div className="text-gray-700 font-mono">
                                  {typeof tool.arguments === "string"
                                    ? tool.arguments
                                    : JSON.stringify(tool.arguments, null, 2)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {step.observation && (
                        <div className="mt-2 space-y-2">
                          {step.observation.results.map((result, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono"
                            >
                              <div className="text-gray-700 whitespace-pre-wrap break-words">
                                {result.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {step.extra?.tool_result_metadata?.tool_use_result && (
                        <div className="mt-2 space-y-2">
                          <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs">
                            <div className="font-semibold text-yellow-700 mb-1">
                              Command Execution Result
                            </div>
                            {step.extra.tool_result_metadata.tool_use_result.stdout && (
                              <div className="mt-2">
                                <div className="text-green-700 text-xs mb-1">STDOUT:</div>
                                <div className="bg-white border border-gray-300 rounded p-2 text-gray-800 font-mono whitespace-pre-wrap break-words text-xs max-h-64 overflow-y-auto">
                                  {step.extra.tool_result_metadata.tool_use_result.stdout}
                                </div>
                              </div>
                            )}
                            {step.extra.tool_result_metadata.tool_use_result.stderr && (
                              <div className="mt-2">
                                <div className="text-red-700 text-xs mb-1">STDERR:</div>
                                <div className="bg-white border border-red-300 rounded p-2 text-red-800 font-mono whitespace-pre-wrap break-words text-xs max-h-64 overflow-y-auto">
                                  {step.extra.tool_result_metadata.tool_use_result.stderr}
                                </div>
                              </div>
                            )}
                            {step.extra.tool_result_metadata.tool_use_result.interrupted && (
                              <div className="mt-2 text-orange-700 text-xs">
                                ⚠ Command was interrupted
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 line-clamp-2">
                      {step.message.substring(0, 200)}
                      {step.message.length > 200 && "..."}
                    </div>
                  )}
                </div>
                <div className="ml-2 text-gray-500">
                  {isExpanded ? "▼" : "▶"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
